import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiError, downloadPdf } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { canVisitPath } from "@/lib/permissions";
import ContactDocs from "@/components/ContactDocs";
import { stagedName } from "@/lib/stagedFiles";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, Pencil, Search, Mail, ChevronDown, ChevronUp, X, IdCard, FileDown } from "lucide-react";
import { toast } from "sonner";

const EMPTY_CONTACT = {
  type: "customer",
  name: "",
  email: "",
  phone: "",
  address_line1: "",
  address_line2: "",
  city: "",
  postcode: "",
  country: "United Kingdom",
  driver_id: "",
  trading_name: "",
  uses_company_vehicle: false,
  uses_own_vehicle: false,
  own_vehicle_reg: "",
  vehicle_reg: "",
  bank_name: "",
  bank_account_name: "",
  bank_sort_code: "",
  bank_account_number: "",
  is_vat_registered: false,
  vat_number: "",
  additional_contacts: [],
  is_active: true,
  notes: "",
};

export default function Contacts() {
  const [contacts, setContacts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [stagedDocs, setStagedDocs] = useState([]);
  const [form, setForm] = useState(EMPTY_CONTACT);
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState("");
  const [showExtras, setShowExtras] = useState(false);
  const urlTab = searchParams.get("tab");
  const initialTab = urlTab === "drivers" ? "subcontractor" : "customer";
  const [tab, setTabState] = useState(initialTab);

  // sync sidebar URL changes -> tab
  useEffect(() => {
    const t = searchParams.get("tab");
    if (t === "drivers" && tab !== "subcontractor") setTabState("subcontractor");
    if (t === "subs" && tab !== "customer") setTabState("customer");
  }, [searchParams, tab]);

  const setTab = (v) => {
    setTabState(v);
    setSearchParams({ tab: v === "subcontractor" ? "drivers" : "subs" }, { replace: true });
  };

  const load = () => api.get("/contacts").then((r) => setContacts(r.data));
  useEffect(() => { load(); }, []);

  // Compute the next unused D4PS-DR### id by looking at every self-employed
  // driver_id already saved. Handles both padded ("D4PS-DR001") and unpadded
  // legacy values, ignores anything that doesn't match the format so the user
  // can still hand-type custom IDs if needed.
  const nextDriverId = () => {
    const rx = /^D4PS-DR0*(\d+)$/i;
    const used = contacts
      .map((c) => (c.driver_id || "").trim())
      .map((s) => { const m = s.match(rx); return m ? parseInt(m[1], 10) : 0; })
      .filter((n) => n > 0);
    const nextNum = (used.length ? Math.max(...used) : 0) + 1;
    return `D4PS-DR${String(nextNum).padStart(3, "0")}`;
  };

  const openCreate = (type) => {
    setEditing(null);
    setStagedDocs([]);
    setForm({
      ...EMPTY_CONTACT,
      type,
      // Only self-employed drivers get an auto-generated ID.
      driver_id: type === "subcontractor" ? nextDriverId() : "",
    });
    setShowExtras(false);
    setOpen(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({ ...EMPTY_CONTACT, ...c, additional_contacts: c.additional_contacts || [] });
    setShowExtras((c.additional_contacts || []).length > 0);
    setOpen(true);
  };

  const save = async () => {
    if (!form.name) return toast.error("Name is required.");
    const payload = {
      ...form,
      additional_contacts: (form.additional_contacts || []).filter((r) => (r.email || "").trim() || (r.phone || "").trim()),
    };
    try {
      let failed = 0;
      if (editing) await api.put(`/contacts/${editing.id}`, payload);
      else {
        const r = await api.post("/contacts", payload);
        for (const f of stagedDocs) {
          const fd = new FormData();
          fd.append("file", f.file, stagedName(f));
          try { await api.post(`/uploads?entity_type=contact&entity_id=${r.data.id}`, fd, { headers: { "Content-Type": "multipart/form-data" } }); }
          catch { failed += 1; }
        }
      }
      if (failed) toast.warning(`Saved, but ${failed} document${failed === 1 ? "" : "s"} failed to upload — open the contact to try again`);
      else toast.success(editing ? "Contact updated" : "Contact added");
      setStagedDocs([]);
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const del = async (c) => {
    if (!window.confirm(`Delete ${c.name}?`)) return;
    try {
      await api.delete(`/contacts/${c.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const filtered = contacts.filter((c) => {
    if (c.type !== tab) return false;
    const q = search.trim().toLowerCase();
    if (!q) return true;
    // status keywords
    if (q === "active") return c.is_active !== false;
    if (q === "inactive" || q === "unactive") return c.is_active === false;
    const hay = `${c.name || ""} ${c.driver_id || ""} ${c.trading_name || ""} ${c.email || ""} ${c.phone || ""} ${c.vat_number || ""} ${c.vehicle_reg || ""}`.toLowerCase();
    return hay.includes(q);
  });

  return (
    <div data-testid="contacts-page" className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Directory</div>
          <h1 className="font-display text-3xl tracking-tight font-semibold text-slate-900 mt-1">
            {tab === "customer" ? "Subcontractors" : "Self-employed"}
          </h1>
          <p className="text-slate-600 mt-2 text-sm">
            {tab === "customer"
              ? "Companies you pay via Purchase Orders. VAT-registered subcontractors auto-show their VAT number on POs."
              : "Self-employed drivers paid via Self-Bills. Toggle active status and store bank details for payment."}
          </p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
          <div className="relative max-w-md w-full">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid="contacts-search"
              placeholder={`Search ${tab === "customer" ? "subcontractors" : "self-employed"} by name, driver ID, "active" or "inactive"...`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 rounded-sm border-slate-300"
            />
          </div>
          <Button data-testid="contact-new-btn" className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white" onClick={() => openCreate(tab)}>
            <Plus className="h-4 w-4 mr-2" /> Add {tab === "customer" ? "Subcontractor" : "Self-employed"}
          </Button>
        </div>

        <TabsContent value="customer">
          <SplitByActive contacts={filtered} onEdit={openEdit} onDelete={del} />
        </TabsContent>
        <TabsContent value="subcontractor">
          <SplitByActive contacts={filtered} onEdit={openEdit} onDelete={del} />
        </TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display">
              {editing ? "Edit Contact" : `Add ${form.type === "customer" ? "Subcontractor" : "Self-employed"}`}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2 flex items-center justify-between rounded-sm border border-slate-200 px-4 py-3 bg-slate-50">
              <div>
                <Label className="text-sm font-medium text-slate-900">Active</Label>
                <p className="text-xs text-slate-500 mt-0.5">
                  Inactive contacts are hidden from job/PO/Self-Bill pickers but kept in history.
                </p>
              </div>
              <Switch
                data-testid="contact-active-toggle"
                checked={form.is_active !== false}
                onCheckedChange={(v) => setForm({ ...form, is_active: v })}
              />
            </div>
            <Field label="Name *" testid="contact-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
            <Field label="Email" testid="contact-email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
            <Field label="Phone" testid="contact-phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
            {form.type === "subcontractor" && (
              <>
                <Field label="Driver ID (auto)" testid="contact-driver-id" value={form.driver_id} onChange={(v) => setForm({ ...form, driver_id: v })} placeholder="D4PS-DR###" />
                <Field label="Trading name" testid="contact-trading" value={form.trading_name} onChange={(v) => setForm({ ...form, trading_name: v })} />

                <div className="md:col-span-2 flex items-center justify-between rounded-sm border border-slate-200 px-4 py-3">
                  <div>
                    <Label className="text-sm font-medium text-slate-900">Company vehicle?</Label>
                    <p className="text-xs text-slate-500 mt-0.5">Driver uses a D4PS-provided vehicle.</p>
                  </div>
                  <Switch
                    data-testid="contact-vehicle-toggle"
                    checked={!!form.uses_company_vehicle}
                    onCheckedChange={(v) => setForm({ ...form, uses_company_vehicle: v, vehicle_reg: v ? form.vehicle_reg : "", ...(v ? { uses_own_vehicle: false, own_vehicle_reg: "" } : {}) })}
                  />
                </div>
                {form.uses_company_vehicle && (
                  <div className="md:col-span-2">
                    <Field label="D4PS vehicle reg" testid="contact-vehicle-reg" value={form.vehicle_reg} onChange={(v) => setForm({ ...form, vehicle_reg: v.toUpperCase() })} />
                  </div>
                )}

                <div className="md:col-span-2 flex items-center justify-between rounded-sm border border-slate-200 px-4 py-3">
                  <div>
                    <Label className="text-sm font-medium text-slate-900">Own vehicle?</Label>
                    <p className="text-xs text-slate-500 mt-0.5">Driver uses their own vehicle.</p>
                  </div>
                  <Switch
                    data-testid="contact-own-vehicle-toggle"
                    checked={!!form.uses_own_vehicle}
                    onCheckedChange={(v) => setForm({ ...form, uses_own_vehicle: v, own_vehicle_reg: v ? form.own_vehicle_reg : "", ...(v ? { uses_company_vehicle: false, vehicle_reg: "" } : {}) })}
                  />
                </div>
                {form.uses_own_vehicle && (
                  <div className="md:col-span-2">
                    <Field label="Own vehicle reg" testid="contact-own-vehicle-reg" value={form.own_vehicle_reg} onChange={(v) => setForm({ ...form, own_vehicle_reg: v.toUpperCase() })} />
                  </div>
                )}

                <div className="md:col-span-2 mt-2 pb-1 border-b border-slate-200">
                  <Label className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Account details</Label>
                  <p className="text-xs text-slate-500 mt-1">Driver&apos;s bank account — printed on their Self-Bill PDF for payment.</p>
                </div>
                <Field label="Bank name" testid="contact-bank-name" value={form.bank_name} onChange={(v) => setForm({ ...form, bank_name: v })} />
                <Field label="Account name" testid="contact-bank-account-name" value={form.bank_account_name} onChange={(v) => setForm({ ...form, bank_account_name: v })} />
                <Field label="Sort code" testid="contact-bank-sort-code" value={form.bank_sort_code} onChange={(v) => setForm({ ...form, bank_sort_code: v })} />
                <Field label="Account number" testid="contact-bank-account-number" value={form.bank_account_number} onChange={(v) => setForm({ ...form, bank_account_number: v })} />
              </>
            )}
            {form.type === "customer" && (
              <>
                <div className="md:col-span-2 flex items-center justify-between rounded-sm border border-slate-200 px-4 py-3">
                  <div>
                    <Label className="text-sm font-medium text-slate-900">VAT registered?</Label>
                    <p className="text-xs text-slate-500 mt-0.5">Show VAT number on Purchase Orders issued to this subcontractor.</p>
                  </div>
                  <Switch
                    data-testid="contact-vat-toggle"
                    checked={!!form.is_vat_registered}
                    onCheckedChange={(v) => setForm({ ...form, is_vat_registered: v, vat_number: v ? form.vat_number : "" })}
                  />
                </div>
                {form.is_vat_registered && (
                  <div className="md:col-span-2">
                    <Field label="VAT number" testid="contact-vat-number" value={form.vat_number} onChange={(v) => setForm({ ...form, vat_number: v })} />
                  </div>
                )}
              </>
            )}
            {form.type === "customer" && (
              <>
                <Field label="Address line 1" testid="contact-addr1" value={form.address_line1} onChange={(v) => setForm({ ...form, address_line1: v })} />
                <Field label="Address line 2" testid="contact-addr2" value={form.address_line2} onChange={(v) => setForm({ ...form, address_line2: v })} />
                <Field label="City" testid="contact-city" value={form.city} onChange={(v) => setForm({ ...form, city: v })} />
                <Field label="Postcode" testid="contact-postcode" value={form.postcode} onChange={(v) => setForm({ ...form, postcode: v })} />
                <Field label="Country" testid="contact-country" value={form.country} onChange={(v) => setForm({ ...form, country: v })} />

                {/* Bank details for subcontractor */}
                <div className="md:col-span-2 mt-2 text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Bank details</div>
                <Field label="Bank name" testid="contact-bank-name-sub" value={form.bank_name} onChange={(v) => setForm({ ...form, bank_name: v })} />
                <Field label="Account name" testid="contact-bank-account-name-sub" value={form.bank_account_name} onChange={(v) => setForm({ ...form, bank_account_name: v })} />
                <Field label="Sort code" testid="contact-bank-sort-code-sub" value={form.bank_sort_code} onChange={(v) => setForm({ ...form, bank_sort_code: v })} />
                <Field label="Account number" testid="contact-bank-account-number-sub" value={form.bank_account_number} onChange={(v) => setForm({ ...form, bank_account_number: v })} />
              </>
            )}

            {/* Other contacts — collapsible (subcontractors only) */}
            <div className={form.type === "customer" ? "md:col-span-2" : "hidden"}>
              <button
                type="button"
                data-testid="contact-toggle-extras"
                onClick={() => setShowExtras((v) => !v)}
                className="w-full flex items-center justify-between rounded-sm border border-slate-200 px-4 py-2.5 bg-slate-50 hover:bg-slate-100 transition-colors"
              >
                <span className="text-sm font-medium text-slate-700 inline-flex items-center gap-2">
                  <Mail className="h-4 w-4 text-slate-500" />
                  Other contacts
                  {(form.additional_contacts || []).length > 0 && (
                    <span className="text-xs text-slate-500">({form.additional_contacts.length})</span>
                  )}
                </span>
                {showExtras ? <ChevronUp className="h-4 w-4 text-slate-500" /> : <ChevronDown className="h-4 w-4 text-slate-500" />}
              </button>
              {showExtras && (
                <div className="mt-3 space-y-2" data-testid="contact-extras-panel">
                  {(form.additional_contacts || []).length === 0 && (
                    <div className="text-xs text-slate-500 px-1">No extra contacts yet. Add as many as you need — e.g. depot, dispatch, accounts.</div>
                  )}
                  {(form.additional_contacts || []).map((row, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                      <Input
                        data-testid={`contact-extra-label-${idx}`}
                        placeholder="Label"
                        value={row.label || ""}
                        onChange={(e) => {
                          const v = e.target.value;
                          setForm((f) => {
                            const next = [...(f.additional_contacts || [])];
                            next[idx] = { ...next[idx], label: v };
                            return { ...f, additional_contacts: next };
                          });
                        }}
                        className="col-span-3 rounded-sm border-slate-300 h-9"
                      />
                      <Input
                        data-testid={`contact-extra-email-${idx}`}
                        type="email"
                        placeholder="email@company.com"
                        value={row.email || ""}
                        onChange={(e) => {
                          const v = e.target.value;
                          setForm((f) => {
                            const next = [...(f.additional_contacts || [])];
                            next[idx] = { ...next[idx], email: v };
                            return { ...f, additional_contacts: next };
                          });
                        }}
                        className="col-span-5 rounded-sm border-slate-300 h-9"
                      />
                      <Input
                        data-testid={`contact-extra-phone-${idx}`}
                        placeholder="Phone"
                        value={row.phone || ""}
                        onChange={(e) => {
                          const v = e.target.value;
                          setForm((f) => {
                            const next = [...(f.additional_contacts || [])];
                            next[idx] = { ...next[idx], phone: v };
                            return { ...f, additional_contacts: next };
                          });
                        }}
                        className="col-span-3 rounded-sm border-slate-300 h-9"
                      />
                      <button
                        type="button"
                        data-testid={`contact-extra-remove-${idx}`}
                        onClick={() => setForm((f) => ({ ...f, additional_contacts: (f.additional_contacts || []).filter((_, i) => i !== idx) }))}
                        className="col-span-1 inline-flex items-center justify-center text-slate-400 hover:text-red-600"
                        title="Remove"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    data-testid="contact-extra-add"
                    className="rounded-sm"
                    onClick={() => setForm((f) => ({ ...f, additional_contacts: [...(f.additional_contacts || []), { label: "", email: "", phone: "" }] }))}
                  >
                    <Plus className="h-3.5 w-3.5 mr-1.5" /> Add contact
                  </Button>
                </div>
              )}
            </div>
            {form.type === "customer" && <ContactDocs contactId={editing?.id} staged={stagedDocs} setStaged={setStagedDocs} vatRegistered={!!form.is_vat_registered} />}
          </div>
          <DialogFooter>
            {editing && form.type === "customer" && (
              <Button data-testid="contact-dialog-pdf" variant="outline" className="rounded-sm mr-auto"
                onClick={() => downloadPdf(`/contacts/${editing.id}/file.pdf`, `subcontractor-file-${(editing.name || "sub").replace(/[^A-Za-z0-9]+/g, "-")}.pdf`)}>
                <FileDown className="h-4 w-4 mr-2" /> Download PDF
              </Button>
            )}
            <Button data-testid="contact-cancel" variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="contact-save" className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white" onClick={save}>
              {editing ? "Update" : "Add"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SplitByActive({ contacts, onEdit, onDelete }) {
  const [showInactive, setShowInactive] = useState(false);
  const active = contacts.filter((c) => c.is_active !== false);
  const inactive = contacts.filter((c) => c.is_active === false);
  if (!inactive.length) return <ContactGrid contacts={active} onEdit={onEdit} onDelete={onDelete} />;
  const open = showInactive || active.length === 0;
  const heading = (label, n, tone, testid) => (
    <div className="flex items-center gap-3 mt-6" data-testid={testid}>
      <span className={`h-2 w-2 rounded-full ${tone}`} />
      <span className="text-xs uppercase tracking-[0.18em] font-semibold text-slate-600">{label}</span>
      <span className="text-xs text-slate-400">{n}</span>
      <span className="flex-1 h-px bg-slate-200" />
    </div>
  );
  return (
    <>
      {heading("Active", active.length, "bg-emerald-500", "selfemp-active-heading")}
      {active.length ? <ContactGrid contacts={active} onEdit={onEdit} onDelete={onDelete} /> : <div className="text-sm text-slate-500 mt-4">None active.</div>}
      <button type="button" onClick={() => setShowInactive((v) => !v)} aria-expanded={open} data-testid="inactive-toggle"
        className="w-full flex items-center gap-3 mt-6 group text-left">
        <ChevronDown className={`h-4 w-4 text-slate-500 transition-transform ${open ? "" : "-rotate-90"}`} />
        <span className="h-2 w-2 rounded-full bg-slate-400" />
        <span className="text-xs uppercase tracking-[0.18em] font-semibold text-slate-600 group-hover:text-slate-900">Inactive</span>
        <span className="text-xs text-slate-400">{inactive.length}</span>
        <span className="flex-1 h-px bg-slate-200" />
        <span className="text-[11px] text-slate-400 group-hover:text-slate-600">{open ? "Hide" : "Show"}</span>
      </button>
      {open && <ContactGrid contacts={inactive} onEdit={onEdit} onDelete={onDelete} />}
    </>
  );
}

function ContactGrid({ contacts, onEdit, onDelete }) {
  if (contacts.length === 0) {
    return (
      <Card className="rounded-sm border border-slate-200 shadow-none p-12 text-center text-sm text-slate-500 mt-4">
        No contacts yet. Add your first one to get started.
      </Card>
    );
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
      {contacts.map((c) => (
        <Card key={c.id} className={`rounded-sm border shadow-none p-5 ${c.is_active === false ? "border-slate-200 bg-slate-50/60 opacity-75" : "border-slate-200"}`} data-testid={`contact-card-${c.id}`}>
          <div className="flex items-start justify-between">
            <div className="min-w-0">
              <div className="font-display text-lg font-medium text-slate-900 truncate">{c.name}</div>
              <Badge className="mt-1 rounded-sm bg-slate-100 text-slate-700 hover:bg-slate-100 font-normal">
                {c.type === "customer" ? "Subcontractor" : "Self-employed"}
              </Badge>
              {c.is_active === false ? (
                <Badge className="mt-1 ml-2 rounded-sm bg-amber-50 text-amber-700 hover:bg-amber-50 font-normal border border-amber-200">
                  Inactive
                </Badge>
              ) : (
                <Badge className="mt-1 ml-2 rounded-sm bg-emerald-50 text-emerald-700 hover:bg-emerald-50 font-normal border border-emerald-200">
                  Active
                </Badge>
              )}
              {c.type === "customer" && c.is_vat_registered && (
                <Badge className="mt-1 ml-2 rounded-sm bg-emerald-50 text-emerald-700 hover:bg-emerald-50 font-normal border border-emerald-200">
                  VAT{c.vat_number ? ` · ${c.vat_number}` : ""}
                </Badge>
              )}
              {c.driver_id && (
                <div className="mt-2 text-xs text-slate-500">ID: {c.driver_id}</div>
              )}
              {c.type === "subcontractor" && c.uses_company_vehicle && c.vehicle_reg && (
                <div className="mt-1 text-xs text-slate-700">Vehicle: <span className="font-medium">{c.vehicle_reg}</span></div>
              )}
              {c.type === "subcontractor" && c.uses_own_vehicle && (
                <div className="mt-1 text-xs text-slate-700">Own vehicle{c.own_vehicle_reg ? <>: <span className="font-medium">{c.own_vehicle_reg}</span></> : ""}</div>
              )}
              {c.type === "subcontractor" && c.bank_account_number && (
                <div className="mt-1 text-xs text-slate-500">{c.bank_name || "Bank"} · {c.bank_sort_code || "—"} · {c.bank_account_number}</div>
              )}
              {c.email && <div className="mt-2 text-sm text-slate-700">{c.email}</div>}
              {c.phone && <div className="text-sm text-slate-700">{c.phone}</div>}
              <div className="mt-2 text-xs text-slate-500 leading-relaxed">
                {[c.address_line1, c.address_line2, [c.city, c.postcode].filter(Boolean).join(", ")].filter(Boolean).join(" · ")}
              </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              {c.type === "subcontractor" && <OpenInHrButton contact={c} />}
              {c.type === "customer" && (
                <Button data-testid={`contact-pdf-${c.id}`} variant="outline" size="sm" className="h-8 rounded-sm text-xs" title="Download full subcontractor file (PDF)"
                  onClick={() => downloadPdf(`/contacts/${c.id}/file.pdf`, `subcontractor-file-${(c.name || "sub").replace(/[^A-Za-z0-9]+/g, "-")}.pdf`)}>
                  <FileDown className="h-3.5 w-3.5 mr-1" /> PDF
                </Button>
              )}
              <Button data-testid={`contact-edit-${c.id}`} variant="ghost" size="icon" className="h-8 w-8 rounded-sm" onClick={() => onEdit(c)}>
                <Pencil className="h-4 w-4" />
              </Button>
              <Button data-testid={`contact-delete-${c.id}`} variant="ghost" size="icon" className="h-8 w-8 rounded-sm text-red-600 hover:text-red-700" onClick={() => onDelete(c)}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}

function OpenInHrButton({ contact }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  if (!canVisitPath(user, "/hr")) return null;
  const open = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/hr/drivers/from-contact/${contact.id}`);
      navigate(`/hr/drivers/${r.data.id}`);
    } catch (e) { toast.error(formatApiError(e)); setBusy(false); }
  };
  return (
    <Button data-testid={`contact-hr-${contact.id}`} variant="outline" size="sm" className="h-8 rounded-sm text-xs" onClick={open} disabled={busy} title="Open this driver's HR file">
      <IdCard className="h-3.5 w-3.5 mr-1" /> HR
    </Button>
  );
}

function Field({ label, value, onChange, testid, placeholder }) {
  return (
    <div>
      <Label className="text-xs text-slate-600">{label}</Label>
      <Input data-testid={testid} value={value || ""} placeholder={placeholder} onChange={(e) => onChange(e.target.value)}
        className="mt-1 rounded-sm border-slate-300" />
    </div>
  );
}
